import { useCallback, useEffect, useState } from 'react';
import { getMe, login as loginRequest, logout as logoutRequest } from '../api/authApi.js';
import { setUnauthorizedHandler } from '../api/adminApi.js';
import type { AdminCurrentUser } from '../api/types.js';

export function useAuth() {
  const [user, setUser] = useState<AdminCurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMe();
      setUser(response.authenticated ? response.user : null);
      setError(null);
    } catch (err) {
      setUser(null);
      setError(err instanceof Error ? err.message : '登录状态检查失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    const response = await loginRequest(username, password);
    if (response.authenticated) {
      setUser(response.user);
      return;
    }
    setUser(null);
    throw new Error('登录失败');
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
  }, []);

  return {
    user,
    loading,
    error,
    authenticated: Boolean(user),
    login,
    logout,
    refresh,
  };
}
